import React, { useRef, useEffect } from 'react';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import GuessInput, { GuessInputRef } from './GuessInput';
import { Card } from '../ui/Card';
import { GameState } from '../../types/game';
import { StockCategoryFilter } from '../../types/stockCategories';
import { cn } from '../../utils/cn';
import { VerticalGuessHistory } from './VerticalGuessHistory';
import { GiveUpButton } from './GiveUpButton';
import { SkipButton } from './SkipButton';
import { useCategoryGame } from '../../contexts/GameContext';
import { useGuessHistorySync } from '../../hooks/useGuessHistorySync';
import { getCurrentGameTimestamp } from '../../config/dateTimeConfig';
import { useFloatingControls } from '../../hooks/useFloatingControls';
import { useLayoutVariant } from '../../hooks/useLayoutVariant';

interface GameControlsProps {
  gameState: GameState;
  currentCategory: StockCategoryFilter;
  category?: StockCategoryFilter;
  isSubmitting: boolean;
  isShaking: boolean;
  stockOptions: any[];
  stockOptionsLoading: boolean;
  stockOptionsError: string | null;
  searchStockOptions: (query: string) => Promise<string[]>;
  onGuessSubmit: (guess: string) => void;
  onShowResults?: () => void;
  onInvalidGuess?: (error: string) => void; // NEW: Handler for invalid guesses
  validationError?: string | null; // NEW: Validation error to display
  onClearValidationError?: () => void; // NEW: Clear validation error
  onCopyResults?: () => void; // NEW: Handler for copying results
  financialMetricsRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  layout?: 'mobile-centered' | 'desktop-sidebar';
}

/**
 * GameControls component - handles user interaction controls with layout variants
 * Includes guess input, submission indicators, and game status
 */
export const GameControls: React.FC<GameControlsProps> = ({
  gameState,
  currentCategory,
  category,
  isSubmitting,
  isShaking,
  stockOptions,
  stockOptionsLoading,
  stockOptionsError,
  searchStockOptions,
  onGuessSubmit,
  onShowResults,
  onInvalidGuess,
  validationError,
  onClearValidationError,
  onCopyResults,
  financialMetricsRef,
  className,
  layout = 'mobile-centered'
}) => {
  const guessInputRef = useRef<GuessInputRef>(null);
  const lastGuessCountRef = useRef(0);

  // Fetch guess history from Supabase for logged-in users (cross-device sync)
  const { guesses: historyGuesses, isLoading: isLoadingHistory } = useGuessHistorySync({
    category: currentCategory,
    gameDate: getCurrentGameTimestamp(),
    localGuesses: gameState.guesses
  });

  // Use floating controls hook to make GuessInput float at bottom (mobile + streamer mode)
  const { isFloating, placeholderRef, userDocked, wouldFloat, toggleDocked } = useFloatingControls({
    disabled: layout !== 'mobile-centered'
  });

  const { isStreamerMode, isActuallyDesktop } = useLayoutVariant();

  // Watch for new incorrect guesses and trigger flash animation + auto-scroll
  useEffect(() => {
    const currentGuessCount = gameState.guesses.length;

    // Check if a new guess was added
    if (currentGuessCount > lastGuessCountRef.current) {
      const latestGuess = gameState.guesses[currentGuessCount - 1];

      // Trigger flash if the guess was incorrect
      if (!latestGuess.isCorrect && guessInputRef.current) {
        guessInputRef.current.triggerErrorFlash();

        // Auto-scroll to chart (top of page) on incorrect guess (mobile only)
        if (layout === 'mobile-centered') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }

      lastGuessCountRef.current = currentGuessCount;
    }
  }, [gameState.guesses, financialMetricsRef, layout]);

  // Get container classes based on layout
  const getContainerClasses = () => {
    const baseClasses = "";
    
    switch (layout) {
      case 'desktop-sidebar':
        return cn(baseClasses, "flex flex-col flex-1 gap-2", className);
      case 'mobile-centered':
      default:
        return cn(baseClasses, "space-y-3", className);
    }
  };

  // Get input container classes based on layout
  const getInputContainerClasses = () => {
    switch (layout) {
      case 'desktop-sidebar':
        return "w-full";
      case 'mobile-centered':
      default:
        return "flex justify-center";
    }
  };

  // Get card padding based on layout
  const getCardPadding = () => {
    switch (layout) {
      case 'desktop-sidebar':
        return "p-3";
      case 'mobile-centered':
      default:
        return "p-4";
    }
  };

  // Get text size based on layout
  const getTextSize = () => {
    switch (layout) {
      case 'desktop-sidebar':
        return "text-xs";
      case 'mobile-centered':
      default:
        return "text-sm";
    }
  };

  // Extract GuessInput element to avoid duplication in placeholder and floating versions
  const guessInputElement = (
    <div className={getInputContainerClasses()}>
      <GuessInput
        ref={guessInputRef}
        onSubmit={onGuessSubmit}
        disabled={gameState.gameStatus !== 'playing' || isSubmitting}
        suggestions={stockOptions}
        category={category || currentCategory}
        onSearchSuggestions={searchStockOptions}
        suggestionsLoading={stockOptionsLoading}
        suggestionsError={stockOptionsError}
        showCategoryContext={true}
        mode={gameState.gameStatus !== 'playing' ? 'results' : 'submit'}
        onResults={onShowResults}
        onInvalidGuess={onInvalidGuess}
        onCopyResults={onCopyResults}
        placeholder={
          stockOptionsLoading
            ? `Loading ${category || currentCategory} companies...`
            : stockOptionsError
            ? "Enter company name (using fallback data)"
            : undefined // Let GuessInput generate category-aware placeholder
        }
        className={cn(
          isShaking && "animate-shake",
          layout === 'desktop-sidebar' && "w-full"
        )}
      />
    </div>
  );

  return (
    <>
      <div className={getContainerClasses()}>
        {/* Guess Input - Placeholder for mobile, always visible for desktop */}
        {layout === 'mobile-centered' ? (
          <div ref={placeholderRef} className="mobile-controls-placeholder relative">
            {!isFloating && guessInputElement}
          </div>
        ) : (
          guessInputElement
        )}

      {/* Submitting Indicator */}
      {isSubmitting && (
        <Card className={cn(
          getCardPadding(),
          "text-center border-primary bg-primary/10"
        )}>
          <div className="flex items-center justify-center gap-2 text-primary">
            <Loader2 className="animate-spin" size={layout === 'desktop-sidebar' ? 14 : 16} />
            <span className={cn(getTextSize(), "font-medium")}>
              {layout === 'desktop-sidebar' ? 'Submitting...' : 'Submitting guess...'}
            </span>
          </div>
        </Card>
      )}

      {/* Action Buttons - only show when game is in progress */}
      {gameState.gameStatus === 'playing' && (
        <div className="grid grid-cols-2 gap-2 mt-4">
          <GiveUpButtonWrapper layout={layout} />
          <SkipButtonWrapper layout={layout} />
        </div>
      )}

      {/* Guess History */}
      <VerticalGuessHistory
        guesses={historyGuesses}
        layout={layout}
        validationError={validationError}
        onClearError={onClearValidationError}
        isLoading={isLoadingHistory}
        className={layout === 'desktop-sidebar' ? 'flex-1 mt-4' : undefined}
      />

      </div>

      {/* Floating GuessInput portal - mobile only when floating */}
      {layout === 'mobile-centered' && isFloating && (
        <div className={cn("mobile-floating-controls relative", isStreamerMode && isActuallyDesktop && "streamer-desktop-float")}>
          {/* Dock toggle button - tap to dock input back to original position */}
          <button
            onClick={toggleDocked}
            className="floating-dock-toggle"
            aria-label="Dock input to original position"
            type="button"
          >
            <ChevronDown size={16} />
          </button>
          {guessInputElement}
        </div>
      )}

      {/* Undock button - show when user docked but controls would otherwise float */}
      {layout === 'mobile-centered' && !isFloating && userDocked && wouldFloat && (
        <button
          onClick={toggleDocked}
          className="floating-dock-toggle-undock"
          aria-label="Enable floating input"
          type="button"
        >
          <ChevronUp size={16} />
        </button>
      )}

    </>
  );
};

/**
 * Wrapper component for GiveUpButton that accesses game context
 * Separated to avoid introducing context dependency in GameControls
 */
const GiveUpButtonWrapper: React.FC<{ layout: 'mobile-centered' | 'desktop-sidebar' }> = ({ layout }) => {
  const { handleGiveUp } = useCategoryGame();

  return (
    <GiveUpButton
      onGiveUp={handleGiveUp}
      className={layout === 'desktop-sidebar' ? 'text-xs' : ''}
    />
  );
};

/**
 * Wrapper component for SkipButton that accesses game context
 * Separated to avoid introducing context dependency in GameControls
 */
const SkipButtonWrapper: React.FC<{ layout: 'mobile-centered' | 'desktop-sidebar' }> = ({ layout }) => {
  const { handleSkip } = useCategoryGame();

  return (
    <SkipButton
      onSkip={handleSkip}
      className={layout === 'desktop-sidebar' ? 'text-xs' : ''}
    />
  );
};
